import { RECORDS_PER_PAGE } from "@root/constants";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FETCH_FAQ,
  DELETE_FAQ,
  ADD_FAQ,
  UPDATE_FAQ,
} from "@root/store/action-types";
import {
  deleteFaq,
  doAddFaq,
  getFaqs,
  updateFaq,
} from "@root/store/thunks/faq";
import { FaqService } from "@root/services/faqs";
import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";

function useFaq() {
  const dispatch = useDispatch();
  const faqService = new FaqService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");
  const [faqLoading, setFaqloading] = useState(false);
  const [addFaqLoading, setAddFaqLoading] = useState(false);
  const [deleteFaqLoading, setDeleteFaqLoading] = useState(false);
  const [updateFaqLoading, setUpdateFaqLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [downloadingFaqs, setDownloadingFaqs] = useState<Set<number>>(
    new Set()
  );

  const [faqData, setFaqData] = useState<any>(null);

  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const pageSize = RECORDS_PER_PAGE;

  const handleFetchUpdatedFaqs = () => {
    const payload = {
      offset: (pageNo - 1) * pageSize,
      limit: pageSize,
      search: search,
    };
    fetchFaqs(payload);
  };

  const onSuccess = (data: any, identifier: any, callaback: any) => {
    if (identifier === FETCH_FAQ) {
      setFaqloading(false);
      setPageCount(data?.count || 0);
      setFaqData(data?.rows || []);
    }
    if (identifier === DELETE_FAQ) {
      setDeleteFaqLoading(false);
      handleFetchUpdatedFaqs();
    }
    if (identifier === ADD_FAQ) {
      setAddFaqLoading(false);
      handleFetchUpdatedFaqs();
    }
    if (identifier === UPDATE_FAQ) {
      setUpdateFaqLoading(false);
      handleFetchUpdatedFaqs();
    }
  };

  const onError = (data: any, identifier: any) => {
    if (identifier === FETCH_FAQ) {
      setFaqloading(false);
    }
    if (identifier === DELETE_FAQ) {
      setDeleteFaqLoading(false);
    }
    if (identifier === ADD_FAQ) {
      setAddFaqLoading(false);
    }
    if (identifier === UPDATE_FAQ) {
      setUpdateFaqLoading(false);
    }
  };

  const addFaq = (payload: any) => {
    setAddFaqLoading(true);
    dispatch(
      doAddFaq({
        payload,
        onSuccess,
        onError,
      })
    );
  };

  const fetchFaqs = (payload: any) => {
    setFaqloading(true);
    dispatch(
      getFaqs({
        onSuccess,
        onError,
        payload,
      })
    );
  };

  const removeFaq = (id: number) => {
    setDeleteFaqLoading(true);
    dispatch(
      deleteFaq({
        id,
        onSuccess,
        onError,
      })
    );
  };

  const doUpdateFaq = (payload: number, id: any) => {
    setUpdateFaqLoading(true);
    dispatch(
      updateFaq({
        payload,
        id,
        onError,
        onSuccess,
      })
    );
  };

  const downloadDocumentsZip = async (
    documentIds: number[],
    faqId?: number,
    fileName = "faq_files.zip"
  ) => {
    if (!documentIds || documentIds.length === 0) return;

    setDownloadingFaqs((prev) => {
      const newSet = new Set(prev);
      if (faqId) {
        newSet.add(faqId);
      }
      return newSet;
    });

    try {
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const response = await faqService.downloadDocumentsZip(
        baseAuthUrl,
        documentIds,
        faqId
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      showNotification({
        type: "error",
        message: t("faq.downloadError"),
      });
    } finally {
      setDownloadingFaqs((prev) => {
        const newSet = new Set(prev);
        if (faqId) {
          newSet.delete(faqId);
        }
        return newSet;
      });
    }
  };

  const isDownloadingFaq = (faqId?: number) => {
    if (!faqId) return false;
    return downloadingFaqs.has(faqId);
  };

  return {
    pageSize,
    faqLoading,
    addFaqLoading,
    deleteFaqLoading,
    updateFaqLoading,
    faqData,
    pageNo,
    setPageNo,
    pageCount,
    setPageCount,
    doUpdateFaq,
    removeFaq,
    fetchFaqs,
    addFaq,
    search,
    setSearch,
    downloadDocumentsZip,
    isDownloadingFaq,
  };
}
export default useFaq;
